import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapInfo from "../../interfaces/cleaning-map/map-info";
import RobotPosition from "../../interfaces/cleaning-map/robot-position";
import { precomputedCoverageColorMap } from "../../interfaces/cleaning-map/pixel-color-type";
import { drawImageToCanvas, getMapCoordinatesFromPoint } from "../../utils/map";

const CoverageCanvas: React.FC<{
  coverageMapInfo: MapInfo | null;
  primaryMapInfo: MapInfo;
  robotPosition: RobotPosition | null;
  robotRadius: number;
  canvasStyle?: React.CSSProperties;
  onCoverageMapLoad?: () => void;
}> = ({ coverageMapInfo, primaryMapInfo, robotPosition, robotRadius, canvasStyle, onCoverageMapLoad }) => {
  const coverageCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const prevPrimaryMapInfoRef = useRef(primaryMapInfo);
  const prevCoverageMapRef = useRef(coverageMapInfo);
  const prevRobotPositionRef = useRef<RobotPosition | null>(null);
  const isCoverageMapLoadedRef = useRef<boolean>(false);

  const [coverageContext, setCoverageContext] = useState(coverageCanvasRef.current?.getContext("2d"));

  const coverageMap = useMemo(() => coverageMapInfo, [coverageMapInfo]);

  useEffect(() => {
    if (!coverageMap || !coverageCanvasRef.current) {
      return;
    }

    const canvas = coverageCanvasRef.current;
    const primaryMapInfoChanged = primaryMapInfo !== prevPrimaryMapInfoRef.current;
    const coverageMapChanged = coverageMap !== prevCoverageMapRef.current;

    if (isCoverageMapLoadedRef.current && coverageContext != null && primaryMapInfoChanged && !coverageMapChanged) {
      // Obstacle map resized but no new coverage image — shift existing canvas content
      const currentContent = coverageContext.getImageData(0, 0, canvas.width, canvas.height);

      // Update canvas size
      canvas.width = primaryMapInfo.width;
      canvas.height = primaryMapInfo.height;

      // Restore saved content, accounting for shift of obstacle map
      const xShift = Math.round(
        (Math.abs(primaryMapInfo.originX) - Math.abs(prevPrimaryMapInfoRef.current.originX)) / primaryMapInfo.resolution
      );
      const yShift = Math.round(
        primaryMapInfo.height -
          (prevPrimaryMapInfoRef.current.height +
            (Math.abs(primaryMapInfo.originY) - Math.abs(prevPrimaryMapInfoRef.current.originY)) /
              primaryMapInfo.resolution)
      );
      coverageContext.putImageData(currentContent, xShift, yShift);
      // After a resize the previous canvas-space position is no longer valid for connecting
      prevRobotPositionRef.current = null;
    } else {
      // Initial draw, or a fresh coverage image arrived (possibly with a new map size) — redraw from server image
      drawImageToCanvas(canvas, primaryMapInfo, coverageMap, true, (context) => setCoverageContext(context));
      isCoverageMapLoadedRef.current = true;
      onCoverageMapLoad?.();
      // Reset connection tracking so we don't connect across a full map reload
      prevRobotPositionRef.current = null;
    }
    prevPrimaryMapInfoRef.current = primaryMapInfo;
    prevCoverageMapRef.current = coverageMap;
  }, [coverageContext, coverageMap, primaryMapInfo]);

  const drawNewPathPoint = useCallback(
    (ctx: CanvasRenderingContext2D, point: RobotPosition, prevPoint: RobotPosition | null = null) => {
      const mapParams = {
        height: primaryMapInfo.height,
        resolution: primaryMapInfo.resolution,
        xPosition: primaryMapInfo.originX,
        yPosition: primaryMapInfo.originY,
      };
      const coordPoint = getMapCoordinatesFromPoint({ x: point.x, y: point.y }, mapParams);

      const distanceMeters =
        prevPoint != null
          ? Math.sqrt(Math.pow(point.x - prevPoint.x, 2) + Math.pow(point.y - prevPoint.y, 2))
          : Infinity;
      const shouldConnect = prevPoint != null && distanceMeters <= 1.0;

      if (shouldConnect) {
        const prevCoordPoint = getMapCoordinatesFromPoint({ x: prevPoint.x, y: prevPoint.y }, mapParams);

        // Draw the connecting stroke with coverage color.
        // Colors are fully opaque (alpha=1), so source-over completely replaces existing pixels —
        // no destination-out erase step is needed and omitting it avoids AA-edge accumulation artefacts.
        ctx.globalCompositeOperation = "source-over";
        ctx.beginPath();
        ctx.moveTo(prevCoordPoint.x, prevCoordPoint.y);
        ctx.lineTo(coordPoint.x, coordPoint.y);
        ctx.lineWidth = robotRadius * 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = `rgba(${precomputedCoverageColorMap[point.coverageMode]})`;
        ctx.stroke();
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.beginPath();
        ctx.arc(coordPoint.x, coordPoint.y, robotRadius, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${precomputedCoverageColorMap[point.coverageMode]})`;
        ctx.fill();
      }
    },
    [primaryMapInfo, robotRadius]
  );

  useEffect(() => {
    if (!coverageContext || robotPosition == null) {
      return;
    }

    drawNewPathPoint(coverageContext, robotPosition, prevRobotPositionRef.current);
    prevRobotPositionRef.current = robotPosition;
  }, [robotPosition, coverageContext, drawNewPathPoint]);

  return <canvas style={{ opacity: 0.4, ...canvasStyle }} ref={coverageCanvasRef} />;
};

export default CoverageCanvas;
